import React, { forwardRef, useCallback, useEffect, useMemo, } from 'react';
import { useMemoizedFn } from 'ahooks';
import { CnPickerView } from '@/components/cn-picker-view';
import { withNativeProps } from '@/utils/with-native-props';
import { mergeProps } from '@/components/cn-picker/with-default-props';
import { usePropsValue } from '@/utils/use-props-value';
import { convertDateToStringArray, convertStringArrayToDate, defaultRenderLabel, generateDatePickerColumns, } from './date-picker-utils';
import { bound } from '@/utils/bound';
const thisYear = new Date().getFullYear();
const defaultProps = {
    min: new Date(new Date().setFullYear(thisYear - 10)),
    max: new Date(new Date().setFullYear(thisYear + 10)),
    precision: 'day',
    renderLabel: defaultRenderLabel,
    defaultValue: null,
};
const CnDatePickerView = forwardRef((p, ref) => {
    const props = mergeProps(defaultProps, p);
    const [value, setValue] = usePropsValue({
        value: props.value,
        defaultValue: props.defaultValue,
        onChange: (v) => {
            if (v === null)
                return;
            props.onConfirm?.(v);
        },
    });
    const now = useMemo(() => new Date(), []);
    const pickerValue = useMemo(() => {
        let date = value ?? now;
        date = new Date(bound(date.getTime(), props.min.getTime(), props.max.getTime()));
        return convertDateToStringArray(date, props.precision);
    }, [value, props.precision, props.min, props.max]);
    const onConfirm = useCallback((val) => {
        setValue(convertStringArrayToDate(val, props.precision), true);
    }, [setValue, props.precision]);
    const onSelect = useMemoizedFn((val) => {
        const date = convertStringArrayToDate(val, props.precision);
        props.onSelect?.(date);
    });
    const columns = useCallback((selected) => generateDatePickerColumns(selected, props.min, props.max, props.precision, props.renderLabel, props.filter), [props.min, props.max, props.precision, props.renderLabel]);
    // 初次渲染时触发一次onSelect，保证用户未操作直接点确认时值的正确性
    useEffect(() => {
        const date = convertStringArrayToDate(pickerValue, props.precision);
        props.onSelect?.(date);
    }, []);
    return withNativeProps(props, React.createElement(CnPickerView, { ref: ref, columns: columns, value: pickerValue, onCancel: props.onCancel, onClose: props.onClose, closeOnMaskClick: props.closeOnMaskClick, visible: props.visible, confirmText: props.confirmText, cancelText: props.cancelText, onConfirm: onConfirm, onChange: onSelect, getContainer: props.getContainer, loading: props.loading, loadingContent: props.loadingContent, afterShow: props.afterShow, afterClose: props.afterClose, onClick: props.onClick, title: props.title, stopPropagation: props.stopPropagation, mouseWheel: props.mouseWheel, destroyOnClose: props.destroyOnClose, forceRender: props.forceRender }, (_, actions) => props.children?.(value, actions)));
});
CnDatePickerView.displayName = 'CnDatePickerView';
export { CnDatePickerView };
